import type { UsableOfferDto } from "../../../api/hooks/useOffers";
import type { ExtensionPreviewDto } from "../../../api/hooks/useStrategySubscriptions";

interface ExtendDiscountWarningProps {
  strategyName: string;
  preview: ExtensionPreviewDto;
  chosenOfferId: string | null;
  offers: UsableOfferDto[];
}

/**
 * Says what extending ends, before she pays: the new year replaces the line, and a discount running on it
 * doesn't move across. Nothing when there is none, or when she has chosen the same offer again.
 */
export function ExtendDiscountWarning({
  strategyName,
  preview,
  chosenOfferId,
  offers,
}: ExtendDiscountWarningProps) {
  if (!preview.replacesDiscount) {
    return null;
  }

  const runningOfferId = preview.runningOfferId;
  const runningOfferName = preview.runningOfferName;
  if (runningOfferId !== null && runningOfferId === chosenOfferId) {
    return null;
  }

  const stillOpen = runningOfferId !== null && offers.some((offer) => offer.id === runningOfferId);

  return (
    <div className="tinted-warning rounded p-3 mt-3">
      {runningOfferName === null ? (
        <>{strategyName} has a discount on it now, and extending ends it.</>
      ) : stillOpen ? (
        <>
          {strategyName} has <strong>{runningOfferName}</strong> on it now. Extending ends it unless
          you choose {runningOfferName} again as the offer.
        </>
      ) : (
        <>
          {strategyName} has <strong>{runningOfferName}</strong> on it now. That offer has closed, so it
          can't go on a new year, and extending ends it.
        </>
      )}
    </div>
  );
}
